import { useState, useEffect, useCallback } from 'react';

type InitialValue<T> = T | (() => T);

function resolveInitialValue<T>(initialValue: InitialValue<T>): T {
  return typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue;
}

export function useLocalStorage<T>(
  key: string,
  initialValue: InitialValue<T>
): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item) {
        return JSON.parse(item) as T;
      }
    } catch {
      // ignore parse errors and fall back to the initial value
    }
    return resolveInitialValue(initialValue);
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch {
      // ignore write errors (e.g. storage unavailable)
    }
  }, [key, storedValue]);

  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setStoredValue((prev) => (typeof value === 'function' ? (value as (prev: T) => T)(prev) : value));
  }, []);

  return [storedValue, setValue];
}
